import { getCurrentEnvironment } from './env.ts';

export function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function isErrorOfType(error: unknown, code: string): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    error.code === code
  );
}

// User-facing CLI error. The top-level handler in `runCli` recognizes the
// `CLIError` name and prints just `error.message` (no stack trace, no Sentry
// capture) so command-shaped failures look like normal CLI output. An empty
// message is the "silent" variant — used by TUI commands that have already
// rendered the error inside an Ink screen and only need the exit code.
export class CLIError extends Error {
  constructor(message: string) {
    super(withEnvironmentLoginHint(message));
    this.name = 'CLIError';
  }
}

export function getLoginCommand(): string {
  return getCurrentEnvironment() === 'dev' ? 'pnpm dev login' : 'lore login';
}

function withEnvironmentLoginHint(message: string): string {
  if (getCurrentEnvironment() !== 'dev') return message;
  return message
    .replaceAll("'lore login'", "'pnpm dev login'")
    .replaceAll('`lore login`', '`pnpm dev login`');
}

// Raised when the API blocks an action (today: sharing a thread) because the
// caller's billing plan doesn't allow it. The CLI surfaces the underlying
// server message plus a direct billing URL so users can self-serve an
// upgrade without grepping docs.
export class BillingBlockedError extends CLIError {
  readonly billingUrl: string;
  readonly serverMessage: string;

  constructor(args: {
    action: string;
    serverMessage: string;
    billingUrl: string;
  }) {
    const trimmedServer = args.serverMessage.trim();
    // Strip a trailing period so we don't double-punctuate when the server
    // already ends one ("…billing plan." + ". Manage…" → "…billing plan..").
    const cleanedServer = trimmedServer.replace(/\.+$/, '');
    const suffix = cleanedServer ? `: ${cleanedServer}` : '';
    super(
      `${args.action} blocked by your billing plan${suffix}. ` +
        `Manage or upgrade your plan: ${args.billingUrl}`,
    );
    this.name = 'BillingBlockedError';
    this.billingUrl = args.billingUrl;
    this.serverMessage = trimmedServer;
  }
}

// Exit the current command with a non-zero status without printing anything
// or reporting to Sentry. Use when a terminal failure has already been
// rendered to the user (e.g. via a progress event or Ink screen) and the
// command just needs to exit non-zero.
export function exitSilently(): never {
  throw new CLIError('');
}
